import {
  Box,
  Card,
  Typography,
  TextField,
  Divider,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Checkbox,
  Autocomplete,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  multiInputFormat,
  multiInputType,
} from "../../../../../../utils/Constant";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";

type FormValues = {
  label: string;
  definedName: string;
  inputType: string;
  inputFormat: string;
  answerOptions: string;
  responseRequired: boolean;
};

const ValidationSchema = yup.object({
  label: yup.string().required("This field can't be empty"),
  inputFormat: yup.string().when("inputType", {
    is: (value: string) => value === "OpenEnd",
    then: (schema) => schema.required("This field can't be empty"),
    otherwise: (schema) => schema.optional(),
  }),
  answerOptions: yup.string().when("inputType", {
    is: (value: string) => value === "SingleSelect" || value === "MultiSelect",
    then: (schema) => schema.required("This field can't be empty"),
    otherwise: (schema) => schema.optional(),
  }),
});

export default function FieldSettings({
  definedNames,
  selectedDefinedNames,
  cellData,
  isEdit,
  onSave,
  onCancel,
  onDelete,
}: any) {
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      label: cellData?.label || "",
      definedName: cellData?.definedName || "",
      inputType: cellData?.inputType || multiInputType[0].value,
      inputFormat: cellData?.inputFormat || "",
      answerOptions: cellData?.answerOptions
        ? cellData?.answerOptions.join(", ")
        : "",
      responseRequired: cellData?.responseRequired ?? false,
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      const data = {
        ...values,
        answerOptions:
          values.answerOptions &&
          values.answerOptions
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean),
      };
      onSave({ ...data, column: cellData.column, row: cellData.row });
    },
  });

  const checkDuplicate = (option: string) => {
    let isSelected = false;
    if (selectedDefinedNames.includes(option)) {
      isSelected = true;
    }
    return isSelected;
  };

  return (
    <Box mt={5} mx={-2}>
      <Card className="card-layout">
        <Typography variant="h5">Field Settings</Typography>
        <form onSubmit={formik.handleSubmit}>
          <Box className="display-name-layout">
            <Typography className="fieldSettingLabel">Input Label</Typography>
            <TextField
              sx={{ ml: 2 }}
              id="label"
              name="label"
              fullWidth
              variant="outlined"
              value={formik.values.label}
              onChange={formik.handleChange}
              error={formik.touched.label && Boolean(formik.errors.label)}
              helperText={formik.touched.label && formik.errors.label}
            />
          </Box>
          <Divider />
          <Box sx={{ display: "flex", my: 2 }}>
            <Box
              sx={{
                width: 150,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
                Defined Name
              </Typography>
              <Tooltip
                title="The unique defined name of the cell in the excel file where you want this question response to go."
                arrow
                placement="top"
              >
                <IconButton sx={{ p: 0 }}>
                  <InfoOutlinedIcon color="primary" />
                </IconButton>
              </Tooltip>
            </Box>
            <Autocomplete
              fullWidth
              sx={{ ml: 2 }}
              disablePortal
              options={definedNames}
              value={
                formik.values.definedName ? `#${formik.values.definedName}` : ""
              }
              onChange={(event, newValue: any) => {
                formik.setFieldValue("definedName", newValue);
              }}
              getOptionDisabled={(option) => checkDuplicate(option)}
              renderInput={(params) => {
                const existingInputProps = params.InputProps ?? {};
                const endAdornment = params.InputProps?.endAdornment;
                return (
                  <TextField
                    name="definedName"
                    {...params}
                    label="Defined Name"
                    error={
                      formik.touched.definedName &&
                      Boolean(formik.errors.definedName)
                    }
                    helperText={
                      formik.touched.definedName && formik.errors.definedName
                    }
                    slotProps={{
                      input: {
                        ...existingInputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <CheckCircleOutlinedIcon color="success" />
                          </InputAdornment>
                        ),
                        endAdornment: endAdornment,
                        sx: { color: "#2E7D32" },
                      },
                    }}
                  />
                );
              }}
            />
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="fieldSettingLabel">Input Type</Typography>
            <Box>
              <FormControl>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  name="inputType"
                  onChange={formik.handleChange}
                  value={formik.values.inputType}
                >
                  {multiInputType.map((type) => (
                    <FormControlLabel
                      key={type.value}
                      value={type.value}
                      control={<Radio />}
                      label={type.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          </Box>
          <Divider />
          {formik.values.inputType === "OpenEnd" && (
            <>
              <Box className="user-name-layout">
                <Typography className="fieldSettingLabel">
                  Input Format
                </Typography>
                <FormControl
                  fullWidth
                  sx={{ ml: 2 }}
                  error={
                    formik.touched.inputFormat &&
                    Boolean(formik.errors.inputFormat)
                  }
                >
                  <Select
                    labelId="inputFormat-select-label"
                    id="inputFormat-select"
                    name="inputFormat"
                    value={formik.values.inputFormat}
                    onChange={formik.handleChange}
                  >
                    {multiInputFormat.map((format) => (
                      <MenuItem value={format.value}>{format.label}</MenuItem>
                    ))}
                  </Select>
                  {Boolean(formik.errors.inputFormat) &&
                    formik.touched.inputFormat && (
                      <FormHelperText>
                        {formik.errors.inputFormat}
                      </FormHelperText>
                    )}
                </FormControl>
              </Box>
              <Divider />
            </>
          )}
          {(formik.values.inputType === "SingleSelect" ||
            formik.values.inputType === "MultiSelect") && (
            <>
              <Box className="user-name-layout">
                <Typography className="fieldSettingLabel">
                  Answer Options
                </Typography>
                <TextField
                  sx={{ ml: 2 }}
                  fullWidth
                  id="answerOptions"
                  name="answerOptions"
                  variant="outlined"
                  value={formik.values.answerOptions}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.answerOptions &&
                    Boolean(formik.errors.answerOptions)
                  }
                  helperText={
                    formik.touched.answerOptions &&
                    Boolean(formik.errors.answerOptions)
                      ? formik.errors.answerOptions
                      : "Separate by comma."
                  }
                />
              </Box>
              <Divider />
            </>
          )}
          <Box className="user-name-layout">
            <Typography className="fieldSettingLabel">
              Additional Options
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  name="responseRequired"
                  checked={formik.values.responseRequired}
                  onChange={formik.handleChange}
                />
              }
              label="Response Required?"
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: isEdit ? "space-between" : "flex-end",
              alignItems: "center",
              mt: 3,
            }}
          >
            {isEdit && (
              <Button
                startIcon={<DeleteOutlinedIcon color="error" />}
                variant="text"
                color="error"
                onClick={() => onDelete(cellData)}
              >
                Delete Input
              </Button>
            )}
            <Box>
              <Button variant="outlined" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{ ml: 3 }}
                onClick={(e) => formik.handleSubmit()}
              >
                Save
              </Button>
            </Box>
          </Box>
        </form>
      </Card>
    </Box>
  );
}
